/**
 * Decision 0113: on a phone, a dismissible card under the workspace bar that offers to install Minga and to turn on
 * notifications, until the person does both or says "Not now". Settings › Account › Notifications has the full page.
 */
import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import {
  currentPushSupport,
  enablePush,
  notificationPermission,
  type PushSupport,
  pushStatusQuery,
} from "@/lib/push"
import { currentPlatform, isStandalone, promptInstall, useInstallState } from "@/lib/pwa"
import { usePhone } from "@/lib/use-media-query"

const DISMISSED_KEY = "minga:pushHintDismissedAt"

export type PushHint = "install" | "ios_install" | "allow" | null

/** Which offer the card makes, if any. */
export const pushHintOf = (s: {
  readonly phone: boolean
  readonly dismissed: boolean
  readonly installed: boolean
  readonly canPrompt: boolean
  readonly ios: boolean
  readonly support: PushSupport
  readonly permission: NotificationPermission
  /** The Worker can push (a VAPID key is set). */
  readonly enabled: boolean
}): PushHint => {
  if (!s.phone || s.dismissed) return null
  if (!s.installed && s.canPrompt) return "install"
  if (!s.installed && s.ios) return s.enabled ? "ios_install" : null
  if (s.enabled && s.support === "supported" && s.permission === "default") return "allow"
  return null
}

export const PUSH_HINT_TEXT: Record<Exclude<PushHint, null>, string> = {
  install: "Add Minga to your Home Screen to open it like an app and get notifications.",
  ios_install:
    "Get notifications on this iPhone: tap Share, then Add to Home Screen, and open Minga from there (iOS 16.4+).",
  allow: "Get a notification when a decision waits on you.",
}

const readDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISSED_KEY) !== null
  } catch {
    return false
  }
}

export function PushHintCard({ workspaceId }: { workspaceId: string }) {
  const phone = usePhone()
  const install = useInstallState()
  const [dismissed, setDismissed] = useState(true)
  const [env, setEnv] = useState({
    ios: false,
    standalone: false,
    support: "unsupported" as PushSupport,
    permission: "default" as NotificationPermission,
  })
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    setDismissed(readDismissed())
    setEnv({
      ios: currentPlatform().ios,
      standalone: isStandalone(),
      support: currentPushSupport(),
      permission: notificationPermission(),
    })
  }, [])
  const status = useQuery({ ...pushStatusQuery, enabled: phone && !dismissed })
  const hint = pushHintOf({
    phone,
    dismissed,
    installed: install.installed || env.standalone,
    canPrompt: install.canPrompt,
    ios: env.ios,
    support: env.support,
    permission: env.permission,
    enabled: status.data?.enabled === true,
  })
  if (!hint) return null
  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()))
    } catch {}
    setDismissed(true)
  }
  const allow = async () => {
    const key = status.data?.publicKey
    if (!key) return
    setError(null)
    try {
      await enablePush(key)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Notifications weren't turned on.")
    }
    setEnv((v) => ({ ...v, permission: notificationPermission() }))
  }
  return (
    <div
      data-push-hint=""
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-rule bg-fill-soft px-4 py-2 text-[13px] text-ink-2"
    >
      <span className="min-w-0 flex-1 basis-56 break-words">{PUSH_HINT_TEXT[hint]}</span>
      {hint === "install" && (
        <Button size="sm" variant="secondary" onClick={() => void promptInstall()}>
          Install
        </Button>
      )}
      {hint === "allow" && (
        <Button size="sm" variant="secondary" onClick={() => void allow()}>
          Turn on
        </Button>
      )}
      <Link
        to="/w/$workspaceId/account/notifications"
        params={{ workspaceId }}
        className="tap text-ink underline-offset-2 hover:underline"
      >
        Settings
      </Link>
      <Button size="sm" variant="ghost" onClick={dismiss}>
        Not now
      </Button>
      {error && <span className="w-full text-blocked">{error}</span>}
    </div>
  )
}
